import { useEffect, useRef } from "react";
import { useReducedMotion } from "framer-motion";

const DENSITY = 0.00005; // drops per px²

function spawn(w, h, fromTop = false) {
  const r = 3 + Math.random() * 8;
  const sliding = Math.random() < 0.3;
  return {
    x: Math.random() * w,
    y: fromTop ? -r * 3 : Math.random() * h,
    r,
    vy: sliding ? 8 + Math.random() * 22 : 0,
    wobble: Math.random() * Math.PI * 2,
    trail: [],
    age: 0,
    nextBurst: 2 + Math.random() * 6,
  };
}

function drawDrop(ctx, x, y, r, stretch, alpha) {
  const ry = r * stretch;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(1, stretch);
  const body = ctx.createRadialGradient(-r * 0.25, -r * 0.3, r * 0.1, 0, 0, r);
  body.addColorStop(0, `rgba(255,255,255,${0.1 * alpha})`);
  body.addColorStop(0.65, `rgba(200,220,255,${0.08 * alpha})`);
  body.addColorStop(0.9, `rgba(20,45,90,${0.3 * alpha})`);
  body.addColorStop(1, `rgba(255,255,255,${0.28 * alpha})`);
  ctx.fillStyle = body;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // specular highlight
  ctx.fillStyle = `rgba(255,255,255,${0.75 * alpha})`;
  ctx.beginPath();
  ctx.ellipse(x - r * 0.35, y - ry * 0.4, r * 0.22, r * 0.14, -0.6, 0, Math.PI * 2);
  ctx.fill();
}

// Water droplets clinging to the "glass" in front of the hero; some slowly slide down leaving a trail.
export default function GlassDrops() {
  const ref = useRef(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    if (reduce) return undefined;
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let drops = [];
    let raf = 0;
    let last = 0;
    let visible = true;

    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      drops = Array.from({ length: Math.round(w * h * DENSITY) }, () => spawn(w, h));
    };

    const frame = (t) => {
      raf = requestAnimationFrame(frame);
      if (!visible || document.hidden) { last = t; return; }
      const dt = Math.min((t - last) / 1000, 0.05);
      last = t;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      ctx.clearRect(0, 0, w, h);

      for (const d of drops) {
        d.age += dt;
        if (d.vy === 0 && d.age > d.nextBurst) {
          d.vy = 10 + Math.random() * 20;
          d.age = 0;
        }
        if (d.vy > 0) {
          d.wobble += dt * 3;
          d.y += d.vy * dt;
          d.x += Math.sin(d.wobble) * 4 * dt;
          d.vy += (Math.random() - 0.45) * 12 * dt;
          d.vy = Math.max(4, Math.min(d.vy, 40));
          d.trail.push({ x: d.x, y: d.y, r: d.r * 0.55, a: 1 });
          if (d.trail.length > 40) d.trail.shift();
          if (Math.random() < 0.004) { d.vy = 0; d.age = 0; d.nextBurst = 4 + Math.random() * 10; }
        }
        for (const p of d.trail) {
          p.a -= dt * 0.35;
          p.r *= 0.998;
        }
        d.trail = d.trail.filter((p) => p.a > 0);
        for (const p of d.trail) drawDrop(ctx, p.x, p.y, p.r, 1, p.a * 0.6);

        if (d.y > h + 30) Object.assign(d, spawn(w, h, true));
        const stretch = d.vy > 0 ? 1.15 + Math.min(d.vy / 60, 0.45) : 1.05;
        drawDrop(ctx, d.x, d.y, d.r, stretch, 1);
      }
    };

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; });
    io.observe(canvas);
    resize();
    raf = requestAnimationFrame((t) => { last = t; frame(t); });
    return () => { cancelAnimationFrame(raf); ro.disconnect(); io.disconnect(); };
  }, [reduce]);

  if (reduce) return null;
  return <canvas ref={ref} className="absolute inset-0 w-full h-full pointer-events-none" data-testid="hero-glass-drops" aria-hidden />;
}
